import { createAsyncThunk } from '@reduxjs/toolkit';
import Moment from 'moment/moment';

import {
  setBillsAllList,
  setBillsCalendarData,
  setBillsList,
} from '@pages/organizer/Finance/components/tabs/Bills/store/bills-slice';
import api from '@setup/api';
import i18next from '@setup/locales/i18n';
import { NotificationService } from '@setup/notifications/SnackbarUtilsConfigurator/SnackbarUtilsConfigurator';

import type { BillsFiltersType } from '@pages/organizer/Finance/components/tabs/Bills/store/bills-types';
import type { WalletPaymentAccountType } from '@pages/organizer/Finance/components/tabs/Wallet/store/wallet-types';
import type { TagsEnum } from '@shared/enums/tags.enum';
import type { ErrorType } from '@shared/interfaces/error.interfaces';
import type { AppDispatch } from '@store/store';
import type { RootState } from '@store/store';

export const getBillsAllList = createAsyncThunk<
  any,
  BillsFiltersType,
  { rejectValue: ErrorType; dispatch: AppDispatch }
>('getBillsList', async (params, { rejectWithValue, dispatch }) => {
  try {
    const result = await api.bills.getAllBills(params);

    dispatch(setBillsAllList({ ...result, data: result.data }));
    return result;
  } catch (e: any) {
    NotificationService.error(i18next.t('general.notifications.defaultError'));
    return rejectWithValue(e.data as ErrorType);
  }
});

export const getBillsList = createAsyncThunk<
  any,
  BillsFiltersType & { date_from?: string; date_to?: string },
  { rejectValue: ErrorType; dispatch: AppDispatch }
>('getBillsList', async (params, { rejectWithValue, dispatch }) => {
  try {
    const result = await api.bills.getBills(params);

    dispatch(setBillsList({ ...result, data: result.data }));
    return result;
  } catch (e: any) {
    NotificationService.error(i18next.t('general.notifications.defaultError'));
    return rejectWithValue(e.data as ErrorType);
  }
});

export const getBillCalendarData = createAsyncThunk<
  any,
  { dateFrom: string; dateTo: string; filters: BillsFiltersType },
  { state: RootState; rejectValue: ErrorType; dispatch: AppDispatch }
>('getBillCalendarData', async (params, { rejectWithValue, dispatch }) => {
  try {
    const getBillsPromise = api.bills.getCalendarBills({
      type: 'transactions',
      date_to: Moment(params.dateTo).format('YYYY-MM-DD'),
      date_from: Moment(params.dateFrom).format('YYYY-MM-DD'),
      ...params.filters,
      query: params.filters?.query,
    });

    return Promise.allSettled([getBillsPromise]).then((results) => {
      const [billResult] = results;

      let billData = { schedule: [] };

      if (billResult.status === 'fulfilled') {
        billData = billResult.value;
      }
      const formattedBillData = billData.schedule.map((item: any) => {
        return {
          ...item,
          model_type: 'recurrence',
          start: Moment(item?.date, 'YYYY-MM-DD HH:mm:ss').format(),
          end: Moment(item?.date, 'YYYY-MM-DD HH:mm:ss').format(),
          allDay: true,
          editable: false,
        };
      });

      const mergedData = [...formattedBillData];

      dispatch(setBillsCalendarData(mergedData));
    });
  } catch (e: any) {
    NotificationService.error(i18next.t('general.notifications.defaultError'));
    return rejectWithValue(e.data as ErrorType);
  }
});

export const updateBill = createAsyncThunk<any, any, { rejectValue: ErrorType }>(
  'updateBill',
  async (params, { rejectWithValue }) => {
    try {
      return await api.bills.updateBill(params);
    } catch (e: any) {
      if (e.status !== 422) {
        NotificationService.error(i18next.t('general.notifications.defaultError'));
      }
      return rejectWithValue(e.data as ErrorType);
    }
  },
);

export const getBill = createAsyncThunk<any, number, { rejectValue: ErrorType }>(
  'getBill',
  async (billId, { rejectWithValue }) => {
    try {
      return await api.bills.getBill(billId);
    } catch (e: any) {
      if (e.status === 403) {
        NotificationService.info(i18next.t('general.notifications.billNotSharedWithYou'));
      } else {
        NotificationService.info(i18next.t('general.notifications.billNotFound'));
      }

      return rejectWithValue(e.data as ErrorType);
    }
  },
);
export const getBillSpending = createAsyncThunk<
  any,
  { billId: number; is_group_summary?: number | null | string },
  { rejectValue: ErrorType }
>('getBillSpending', async (params, { rejectWithValue }) => {
  try {
    return await api.bills.getBillSpending(params);
  } catch (e: any) {
    if (e.status === 403) {
      NotificationService.info(i18next.t('general.notifications.billNotSharedWithYou'));
    } else {
      NotificationService.info(i18next.t('general.notifications.billNotFound'));
    }

    return rejectWithValue(e.data as ErrorType);
  }
});

export const getComingUpRecurrence = createAsyncThunk<
  { data: WalletPaymentAccountType[]; meta: { net_balance: number } },
  { hubs?: TagsEnum[] },
  { rejectValue: ErrorType; dispatch: AppDispatch }
>('getComingUpRecurrence', async (params, { rejectWithValue }) => {
  try {
    return await api.bills.getComingUpRecurrence(params);
  } catch (e: any) {
    NotificationService.error(i18next.t('general.notifications.defaultError'));
    return rejectWithValue(e.data as ErrorType);
  }
});

export const getRecurrencesChartInfo = createAsyncThunk<
  { data: WalletPaymentAccountType[]; meta: { net_balance: number } },
  undefined,
  { rejectValue: ErrorType; dispatch: AppDispatch }
>('getRecurrencesChartInfo', async (_, { rejectWithValue }) => {
  try {
    return await api.bills.getRecurrencesChartInfo();
  } catch (e: any) {
    NotificationService.error(i18next.t('general.notifications.defaultError'));
    return rejectWithValue(e.data as ErrorType);
  }
});
export const getRecurrenceExpensesOverview = createAsyncThunk<
  any,
  { date_from: string | null; date_to: string | null },
  { rejectValue: ErrorType; dispatch: AppDispatch }
>('getRecurrenceExpensesOverview', async (params, { rejectWithValue }) => {
  try {
    return await api.bills.getRecurrenceExpensesOverview(params);
  } catch (e: any) {
    NotificationService.error(i18next.t('general.notifications.defaultError'));
    return rejectWithValue(e.data as ErrorType);
  }
});

export const getRecurrenceBreakdownByType = createAsyncThunk<
  any,
  { date_from: string | null; date_to: string | null },
  { rejectValue: ErrorType; dispatch: AppDispatch }
>('getRecurrenceBreakdownByType', async (params, { rejectWithValue }) => {
  try {
    return await api.bills.getRecurrenceBreakdownByType(params);
  } catch (e: any) {
    NotificationService.error(i18next.t('general.notifications.defaultError'));
    return rejectWithValue(e.data as ErrorType);
  }
});

export const getBillTransactions = createAsyncThunk<
  any,
  { billId: number; date_from: any; date_to: any; is_group_summary?: number | null | string } & any,
  { rejectValue: ErrorType }
>('getBillTransactions', async (params, { rejectWithValue }) => {
  try {
    return await api.bills.getBillTransactions(params);
  } catch (e: any) {
    if (e.status === 403) {
      NotificationService.info(i18next.t('general.notifications.billNotSharedWithYou'));
    } else {
      NotificationService.info(i18next.t('general.notifications.billNotFound'));
    }

    return rejectWithValue(e.data as ErrorType);
  }
});

export const getInsurancesItems = createAsyncThunk<any, number, { rejectValue: ErrorType }>(
  'getInsurancesItems',
  async (insuranceId, { rejectWithValue }) => {
    try {
      return await api.bills.getInsurancesItems(insuranceId);
    } catch (e: any) {
      NotificationService.error(i18next.t('general.notifications.defaultError'));
      return rejectWithValue(e.data as ErrorType);
    }
  },
);

export const getPayment = createAsyncThunk<any, any, { rejectValue: ErrorType; dispatch: AppDispatch }>(
  'getMeeting',
  async (props, { rejectWithValue }) => {
    try {
      return await api.payment.getTransactions(props);
    } catch (e: any) {
      if (e.status === 403) {
        NotificationService.info(i18next.t('general.notifications.notAssignedToPayment'));
      } else {
        NotificationService.info(i18next.t('general.notifications.paymentNotFound'));
      }

      return rejectWithValue(e.data as ErrorType);
    }
  },
);

export const deleteBill = createAsyncThunk<
  number,
  { billId: number; confirmation_status?: string },
  { rejectValue: ErrorType; dispatch: AppDispatch }
>('deleteBill', async (params, { rejectWithValue }) => {
  try {
    return await api.bills.deleteBill(params).then(() => params.billId);
  } catch (e: any) {
    NotificationService.error(i18next.t('general.notifications.defaultError'));
    return rejectWithValue(e.data as ErrorType);
  }
});
